import { useState, useCallback, useEffect } from 'react';
import { mutate } from 'swr';
import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axiosInstance from '@/lib/axios';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormPasswordField } from '@/components/forms/form-password-field';
import { FormPhoneField } from '@/components/forms/form-phone-field';
import { toast } from '@/lib/toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTenant } from '@/providers/tenant-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormOtpField } from '@/components/forms/form-otp-field';
import { AuthPolicyNotice } from '@/components/auth/AuthPolicyNotice';
import { clearLegacyLoginStorageKeys } from '@/lib/clear-legacy-login-storage';
import { markPendingNotificationPromptIfNative } from '@/lib/notification-prompt-state';
import {
  getActiveInviteCode,
  getLoginPathForInvite,
  persistInviteCode,
  resolvePostAuthDestination,
  resolveSafeRedirectPath,
} from '@/lib/invite-flow';

function collapseName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

const requiredName = (message: string) =>
  z
    .string()
    .transform(collapseName)
    .pipe(z.string().min(2, message));

const registerSchema = z.object({
  firstName: requiredName('First name is required'),
  middleName: z.string().transform((value) => collapseName(value)),
  lastName: requiredName('Last name is required'),
  email: z.string().trim().email('Invalid email address'),
  phone: z.string().trim().min(10, 'Phone must be at least 10 digits'),
  otp: z.string().trim().length(6, 'OTP must be 6 digits'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  inviteCode: z.string().transform((value) => value.trim()),
});

export default function RegisterPage() {
  const navigate = useNavigate();
  const { tenant, isWhitelabel } = useTenant();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('inviteCode');
  const activeInviteCode = getActiveInviteCode(inviteCode);
  const redirectPath = resolveSafeRedirectPath(searchParams.get('redirect'));

  useEffect(() => {
    clearLegacyLoginStorageKeys();
  }, []);

  useEffect(() => {
    if (inviteCode) persistInviteCode(inviteCode);
  }, [inviteCode]);

  const emailParam = searchParams.get('email');
  const firstNameParam = searchParams.get('firstName');
  const middleNameParam = searchParams.get('middleName');
  const lastNameParam = searchParams.get('lastName');

  const [otpSent, setOtpSent] = useState(() => {
    return sessionStorage.getItem('register_otp_sent') === 'true';
  });
  const [otpVerified, setOtpVerified] = useState(() => {
    return sessionStorage.getItem('register_otp_verified') === 'true';
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(() => !otpVerified);

  const getSavedFormValues = () => {
    try {
      const saved = sessionStorage.getItem('register_form_values');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedValues = getSavedFormValues();

  const form = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: savedValues || {
      firstName: firstNameParam || '',
      middleName: middleNameParam || '',
      lastName: lastNameParam || '',
      email: emailParam || '',
      phone: searchParams.get('phone') || '',
      otp: '',
      password: '',
      inviteCode: inviteCode || '',
    },
  });

  const { trigger, getValues, control, watch, setValue } = form;

  const [lastPhoneSent, setLastPhoneSent] = useState<string>(() => {
    if (savedValues && savedValues.phone) return savedValues.phone;
    return searchParams.get('phone') || '';
  });
  const [hasAutoSent, setHasAutoSent] = useState(false);

  const phoneValue = watch('phone');

  useEffect(() => {
    if (phoneValue !== lastPhoneSent) {
      setOtpSent(false);
      setOtpVerified(false);
    }
  }, [phoneValue, lastPhoneSent]);

  useEffect(() => {
    const subscription = watch((value) => {
      sessionStorage.setItem('register_form_values', JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    sessionStorage.setItem('register_otp_sent', otpSent.toString());
    sessionStorage.setItem('register_otp_verified', otpVerified.toString());
  }, [otpSent, otpVerified]);

  const clearSessionState = () => {
    sessionStorage.removeItem('register_otp_sent');
    sessionStorage.removeItem('register_otp_verified');
    sessionStorage.removeItem('register_form_values');
  };

  const sendOtp = useCallback(async () => {
    const isValidPhone = await trigger('phone');
    if (!isValidPhone) return;

    setIsLoading(true);
    try {
      const currentPhone = getValues('phone').trim();
      await axiosInstance.post('/api/auth/otp', { phone: currentPhone });
      setOtpSent(true);
      setLastPhoneSent(currentPhone);
      toast.success('OTP Sent', { description: 'Please check your WhatsApp.' });
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      toast.error('Error', { description: err.response?.data?.error || 'Failed to send OTP' });
    } finally {
      setIsLoading(false);
    }
  }, [trigger, getValues]);

  interface VerifyOtpResponse {
    message: string;
    exists: boolean;
    needsRegistration?: boolean;
    token?: string;
    joinedCommunityId?: string | null;
    user?: {
      firstName: string;
      middleName: string;
      lastName: string;
      email: string;
      phone: string;
    };
  }

  const verifyOtp = useCallback(
    async (otpOverride?: string) => {
      const otp = (otpOverride ?? getValues('otp')).trim();
      if (!otp) {
        form.setError('otp', { message: 'Enter OTP' });
        return;
      }
      setIsLoading(true);
      try {
        const response = await axiosInstance.put<VerifyOtpResponse>('/api/auth/otp', {
          phone: getValues('phone').trim(),
          otp,
          inviteCode: activeInviteCode || undefined,
        });

        const { exists, needsRegistration, token, user, joinedCommunityId } = response.data;

        if (exists && !needsRegistration) {
          if (token) {
            localStorage.setItem('token', token);
          }
          toast.success('Welcome back', { description: 'Logged in successfully.' });
          clearSessionState();
          markPendingNotificationPromptIfNative();
          await mutate('/api/user');
          navigate(
            resolvePostAuthDestination({
              joinedCommunityId,
            })
          );
          return;
        }

        setOtpVerified(true);
        toast.success('Verified', { description: 'Phone number verified.' });
        setShowVerificationDialog(false);

        if (exists && needsRegistration && user) {
          form.reset({
            ...form.getValues(),
            firstName: user.firstName || '',
            middleName: user.middleName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          });
        }
      } catch (error) {
        const err = error as AxiosError<{ error: string }>;
        toast.error('Error', { description: err.response?.data?.error || 'Invalid OTP' });
      } finally {
        setIsLoading(false);
      }
    },
    [getValues, form, navigate, activeInviteCode]
  );

  useEffect(() => {
    const phone = searchParams.get('phone');
    if (phone && !otpSent && !otpVerified && !hasAutoSent) {
      setHasAutoSent(true);
      sendOtp();
    }
  }, [searchParams, otpSent, otpVerified, sendOtp, hasAutoSent]);

  async function onSubmit(values: z.infer<typeof registerSchema>) {
    if (!otpVerified) {
      toast.error('Action Required', { description: 'Please verify your phone number first.' });
      return;
    }
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/api/auth/register', values);
      toast.success('Success', { description: 'Account created successfully!' });
      if (response.data.token) localStorage.setItem('token', response.data.token);
      try {
        sessionStorage.setItem('post_register_add_business_prompt', 'true');
      } catch {
        // ignore storage access errors
      }
      sessionStorage.removeItem('login_redirect_url');
      clearSessionState();
      markPendingNotificationPromptIfNative();
      await mutate('/api/user');
      navigate(
        resolvePostAuthDestination({
          joinedCommunityId: response.data.joinedCommunityId,
          fallback: redirectPath || '/dashboard',
        })
      );
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      toast.error('Registration Failed', {
        description: err.response?.data?.error || 'Unknown error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 py-12 md:p-8 bg-background">
      <Link
        to="/"
        className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors z-20"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to home
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl relative z-10"
      >
        <Card className="w-full border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              {isWhitelabel && tenant?.logoWithName ? (
                <img
                  src={tenant.logoWithName}
                  alt={tenant.name}
                  className="h-14 w-auto object-contain"
                />
              ) : (
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-14 h-14 object-contain"
                />
              )}
            </div>
            <CardTitle className="text-2xl md:text-3xl">Create your account</CardTitle>
            <CardDescription className="text-base">
              Join our community and expand your network
            </CardDescription>
            <p className="text-sm text-muted-foreground pt-2">
              Already have an account?{' '}
              <Link
                to={activeInviteCode ? getLoginPathForInvite(activeInviteCode) : '/login'}
                className="font-semibold text-primary hover:underline underline-offset-4"
              >
                Log in
              </Link>
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className={cn(!otpVerified && 'opacity-50 pointer-events-none')}>
                  <h3 className="text-base font-semibold mb-4 flex items-center gap-2 text-foreground border-b border-border/60 pb-2">
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary text-xs font-bold">
                      1
                    </span>
                    Personal information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <FormInputField
                      control={control}
                      name="firstName"
                      label="First Name"
                      placeholder="John"
                      required
                      onBlur={(event) => {
                        setValue('firstName', collapseName(event.target.value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                    <FormInputField
                      control={control}
                      name="middleName"
                      label="Middle Name"
                      placeholder="Quincy"
                      onBlur={(event) => {
                        setValue('middleName', collapseName(event.target.value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                    <FormInputField
                      control={control}
                      name="lastName"
                      label="Last Name"
                      placeholder="Doe"
                      required
                      onBlur={(event) => {
                        setValue('lastName', collapseName(event.target.value), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>

                  <div className="mt-6">
                    <FormInputField
                      control={control}
                      name="email"
                      label="Email Address"
                      type="email"
                      placeholder="john@example.com"
                      icon={<Mail className="h-4 w-4" />}
                      required
                      onBlur={(event) => {
                        setValue('email', event.target.value.trim(), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>

                  <div className="mt-6">
                    <FormPasswordField
                      control={control}
                      name="password"
                      label="Set a Password"
                      placeholder="Enter a new password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold mt-8"
                  disabled={isLoading || !otpVerified}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating account…
                    </>
                  ) : (
                    'Create account'
                  )}
                </Button>
              </form>

              <Dialog
                open={showVerificationDialog}
                onOpenChange={(open) => {
                  if (!open && !otpVerified) return;
                  setShowVerificationDialog(open);
                }}
              >
                <DialogContent
                  className="sm:max-w-[425px]"
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onEscapeKeyDown={(e) => e.preventDefault()}
                  hideCloseButton
                >
                  <DialogHeader>
                    <DialogTitle className="text-xl">Verify your phone</DialogTitle>
                    <DialogDescription>
                      We need to verify your phone number via WhatsApp before you can proceed.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6 pt-2">
                    <div className="flex flex-col gap-3">
                      <FormPhoneField
                        control={control}
                        name="phone"
                        label="WhatsApp number"
                        placeholder="Enter phone number"
                        disabled={otpVerified || isLoading}
                      />
                      {!otpVerified && (
                        <Button
                          type="button"
                          onClick={sendOtp}
                          disabled={isLoading}
                          variant={otpSent ? 'outline' : 'default'}
                          className="h-11 font-semibold gap-2"
                        >
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                          ) : (
                            <MessageSquare className="w-4 h-4 shrink-0" />
                          )}
                          {otpSent ? 'Resend OTP' : 'Send via WhatsApp'}
                        </Button>
                      )}
                    </div>

                    <AnimatePresence>
                      {otpSent && !otpVerified && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-4 overflow-hidden"
                        >
                          <FormOtpField
                            control={control}
                            name="otp"
                            label="Enter OTP"
                            onComplete={(code) => void verifyOtp(code)}
                          />
                          <Button
                            type="button"
                            onClick={() => void verifyOtp()}
                            disabled={isLoading}
                            className="h-11 w-full font-semibold"
                          >
                            {isLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Verify code'
                            )}
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </DialogContent>
              </Dialog>
            </Form>
            <AuthPolicyNotice variant="register" />
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Powered by <span className="font-medium text-foreground/80">Milora</span>
        </p>
      </motion.div>
    </div>
  );
}
